import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Play, DownloadSimple, ShareNetwork, FilmStrip, ArrowRight, Heart, QrCode, UploadSimple, Microphone, Eye, DeviceMobile } from '@phosphor-icons/react';
import { EventDropWatermark } from '../components/EventDropLogo';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FilmSharePage = () => {
  const { slug } = useParams();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => { fetchFilm(); }, [slug]);

  const fetchFilm = async () => {
    try { const res = await axios.get(`${API}/films/slug/${slug}`); setFilm(res.data); }
    catch {}
    finally { setLoading(false); }
  };

  const getVideoUrl = () => film?.video_url ? (film.video_url.startsWith('/api') ? `${BACKEND_URL}${film.video_url}` : film.video_url) : '';
  const getHighlightUrl = () => film?.highlight_url ? (film.highlight_url.startsWith('/api') ? `${BACKEND_URL}${film.highlight_url}` : film.highlight_url) : '';

  const handleShare = (platform) => {
    const url = `${window.location.origin}/film/${slug}`;
    const text = `Watch ${film?.event_name}'s event film!`;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    const url = `${window.location.origin}/film/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${film?.event_name} — Event Film`, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/events/${film?.event_id}/film/download`);
      if (!res.ok) { window.open(getVideoUrl(), '_blank'); return; }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${film.event_name.replace(/\s/g, '_')}_film.mp4`;
      link.click();
    } catch { window.open(getVideoUrl(), '_blank'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#15161B] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#B89762]/50 border-t-transparent"></div>
    </div>
  );

  if (!film) return (
    <div className="min-h-screen bg-[#15161B] flex items-center justify-center text-center px-6">
      <div>
        <FilmStrip size={64} weight="bold" className="text-gray-700 mx-auto mb-4" />
        <h1 className="text-3xl font-black text-white mb-2">Film Not Available</h1>
        <p className="text-gray-500 mb-8">This event film hasn't been created yet.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
          Create Your Own Event Film
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#15161B]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="film-share-page">

      {/* HERO VIDEO */}
      <div className="max-w-5xl mx-auto px-4 pt-10">
        <div className="mb-4">
          <BackButton fallbackHref="/" label="Back" variant="subtle" testId="film-share-back" />
        </div>
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video shadow-2xl shadow-black/50" data-testid="film-video-container">
          {playing ? (
            <video src={getVideoUrl()} controls autoPlay className="w-full h-full object-contain" data-testid="film-video-player" />
          ) : (
            <div onClick={() => setPlaying(true)} className="w-full h-full flex items-center justify-center cursor-pointer group relative" style={{ background: 'linear-gradient(135deg, #1a0525, #0d0d0d, #1a0a1e)' }}>
              <div className="absolute inset-0 flex items-center justify-center"><FilmStrip size={140} weight="thin" className="text-white/5" /></div>
              <div className="relative z-10 w-24 h-24 rounded-full flex items-center justify-center group-hover:scale-110 transition-all duration-300 border border-white/20" style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.3), rgba(255,77,141,0.3))', backdropFilter: 'blur(12px)' }}>
                <Play size={40} weight="fill" className="text-white ml-1" />
              </div>
              {/* Overlay badge */}
              <div className="absolute bottom-6 left-6 px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
                <span className="text-white/80 text-sm font-medium">Created from {film.photo_count} guest moments</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FILM INFO */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B89762]/50/20 bg-[#B89762]/5 text-[#B89762] text-sm font-semibold mb-5">
            <Heart size={14} weight="fill" /> Event Film
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3 tracking-tight" data-testid="film-event-name">{film.event_name}</h1>

          {/* Social proof */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500 text-sm" data-testid="social-proof">
            {film.event_date && <span>{film.event_date}</span>}
            {film.duration && <span>{film.duration}</span>}
            <span>Captured by {film.photo_count} moments</span>
            <span className="flex items-center gap-1"><Eye size={14} /> Watched {film.film_views || 0} times</span>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          <button onClick={handleDownload} className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-gray-900 font-bold hover:bg-gray-100 transition-colors text-lg" data-testid="download-film-share">
            <DownloadSimple size={22} weight="bold" /> Download Film
          </button>
          <button onClick={handleNativeShare} className="flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white text-lg transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #B89762, #A033D8)' }} data-testid="share-film-native">
            <ShareNetwork size={22} weight="bold" /> Share Film
          </button>
        </div>

        {/* Social share buttons */}
        <div className="flex justify-center gap-3 mb-12">
          {[
            { name: 'WhatsApp', key: 'whatsapp', bg: 'bg-green-600 hover:bg-green-700' },
            { name: 'Facebook', key: 'facebook', bg: 'bg-blue-600 hover:bg-blue-700' },
            { name: 'Twitter', key: 'twitter', bg: 'bg-gray-800 hover:bg-gray-700' },
          ].map(p => (
            <button key={p.key} onClick={() => handleShare(p.key)} className={`px-6 py-2.5 rounded-full text-white text-sm font-semibold transition-colors ${p.bg}`} data-testid={`share-${p.key}`}>
              {p.name}
            </button>
          ))}
        </div>

        {/* MINI GALLERY — "Were you there?" */}
        {film.gallery?.length > 0 && (
          <div className="mb-12" data-testid="mini-gallery-section">
            <button onClick={() => setShowGallery(!showGallery)} className="w-full text-center mb-4 group">
              <p className="text-white/60 text-base font-medium group-hover:text-white/80 transition-colors">
                Were you there? Find your moment inside
              </p>
            </button>
            {showGallery && (
              <div className="grid grid-cols-4 gap-2 animate-fadeIn" data-testid="mini-gallery-grid">
                {film.gallery.map((url, i) => (
                  <div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-900">
                    <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HOW THIS WAS MADE */}
        <div className="mb-12 py-10 border-t border-b border-white/5" data-testid="how-made-section">
          <h3 className="text-center text-xl font-bold text-white mb-8">How this film was made</h3>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {[
              { icon: <QrCode size={20} weight="bold" />, label: 'Guests scanned QR', color: '#C29D52' },
              { icon: <UploadSimple size={20} weight="bold" />, label: 'Uploaded photos & videos', color: '#38BDF8' },
              { icon: <Microphone size={20} weight="bold" />, label: 'Left messages', color: '#26A69A' },
              { icon: <FilmStrip size={20} weight="bold" />, label: 'Auto-edited into a film', color: '#A033D8' },
            ].map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#15161B] border border-white/10">
                  <span style={{ color: step.color }}>{step.icon}</span>
                  <span className="text-sm text-gray-300">{step.label}</span>
                </div>
                {i < 3 && <span className="text-gray-700 text-lg">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* HIGHLIGHT REEL PREVIEW */}
        {film.highlight_ready && (
          <div className="mb-12 text-center" data-testid="highlight-preview-section">
            <div className="inline-flex items-center gap-2 mb-3">
              <DeviceMobile size={18} weight="bold" className="text-[#B89762]" />
              <span className="text-sm font-bold text-[#B89762]">HIGHLIGHT REEL</span>
            </div>
            <p className="text-gray-400 text-base mb-4">30-second shareable version</p>
            <div className="flex justify-center">
              <div className="rounded-2xl overflow-hidden bg-black shadow-xl" style={{ width: 180, height: 320 }}>
                <video src={getHighlightUrl()} controls className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        )}

        {/* CONVERSION BLOCK */}
        <div className="text-center py-12 border-t border-white/5" data-testid="conversion-block">
          <h2 className="text-3xl font-black text-white mb-3 tracking-tight">Relive your event through<br />your guests' eyes</h2>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400 mb-8">
            <span>Moments you didn't see</span>
            <span>·</span>
            <span>Messages you'll keep forever</span>
            <span>·</span>
            <span>Ready the next morning</span>
          </div>
          <Link to="/checkout?package=capture" className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl font-bold text-white text-lg transition-all hover:scale-105 shadow-lg" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', boxShadow: '0 0 30px rgba(108,92,231,0.15)' }} data-testid="create-own-film-cta">
            Create your own event — from £39 <ArrowRight size={20} weight="bold" />
          </Link>
          <p className="text-sm text-gray-600 mt-4">Starting at £39 · One-time payment</p>
        </div>

        {/* SHARE PROMPT */}
        <div className="text-center pb-8" data-testid="share-prompt">
          <p className="text-gray-600 text-sm mb-3">Send this to your guests — let them relive it too</p>
          <button onClick={handleNativeShare} className="px-6 py-2.5 rounded-full text-sm font-semibold text-white/60 border border-white/10 hover:text-white/80 hover:border-white/20 transition-colors">
            Copy Link
          </button>
        </div>

        {/* VIRAL HOOK — every guest viewing a shared film sees the EventDrop pitch */}
        <div className="mx-4 sm:mx-8 mb-8 rounded-2xl p-6 sm:p-8 text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.18), rgba(255,204,0,0.10))', border: '1px solid rgba(255,204,0,0.25)' }} data-testid="viral-hook">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-2" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Love this?</p>
          <h3 className="text-xl sm:text-2xl font-black text-white mb-2">Start yours in 60 seconds</h3>
          <p className="text-sm text-gray-400 mb-5 max-w-md mx-auto">Every one of your guests gets their own private film like this — no app, no faff. Just a QR at your event.</p>
          <a href="https://eventdrop.co/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-black" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)' }} data-testid="viral-hook-cta">
            See how it works →
          </a>
        </div>

        {/* FOOTER */}
        <div className="text-center pt-8 pb-4 border-t border-white/5">
          <div className="flex items-center justify-center mb-2">
            <EventDropWatermark />
          </div>
          <p className="text-gray-700 text-xs">&copy; 2026 EventDrop. All rights reserved.</p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default FilmSharePage;
